import type { Spritesheet } from "pixi.js";
import { SpineArtboard } from "@slot/presentation/rive-spine";
import { loadGameVisualAssets } from "../assets/GameVisualAssets";
import { CAPTAIN_TUNING } from "../tuning/characterTuning";
import { captainArtSpec } from "../views/character/captainArt";
import { COMPOSITION_ROOT, GAME_LOGO, GameScene } from "./GameScene";
import { GameModals } from "./GameModals";
import type { GameSceneHandle, SceneDeps } from "@slot/game-shell";

/** This game's scene as the shell sees it, plus the two objects its round presentation reads. */
export interface SlotCannonScene extends GameSceneHandle {
  game: GameScene;
  gameModals: GameModals;
  /** The symbols atlas (the sprite win look resolves its alt frames from it). */
  symbolsAtlas: Spritesheet;
}

/** The scene and the game modals from the loaded assets (the art layout is this game's). */
export function createScene(deps: SceneDeps): SlotCannonScene {
  const assets = loadGameVisualAssets({ payload: deps.payload, log: deps.log });
  const game = new GameScene({
    assets,
    chromeTaskRunner: deps.services.chromeTaskRunner,
    featureStateStore: deps.services.featureStateStore,
    viewport: deps.viewport,
    contentArea: deps.contentArea,
    presentationTicker: deps.presentationTicker,
    spineDriver: deps.spineDriver,
    resolution: deps.renderer.resolution,
    localization: deps.services.localization,
    log: deps.log,
  });
  // The captain's art loads now, off the start (no round waits for it): he steps in once it is in.
  if (assets.captainArt) {
    void SpineArtboard.loadAll(assets.captainArt, [captainArtSpec(CAPTAIN_TUNING)], { renderer: deps.renderer, log: deps.log }).then(([art]) => {
      if (art) game.captainView.setArt(art);
    });
  }
  const gameModals = new GameModals({
    assets,
    localization: deps.services.localization,
    runner: deps.services.chromeTaskRunner,
    viewport: deps.viewport,
    spineDriver: deps.spineDriver,
  });
  return { node: game, modalsNode: gameModals, compositionRoot: game.compositionRoot, compositionSize: { width: COMPOSITION_ROOT.width, height: COMPOSITION_ROOT.height }, logo: GAME_LOGO, game, gameModals, symbolsAtlas: assets.symbols };
}
